/* eslint-disable jsx-a11y/anchor-has-content */
import { makeStyles } from '@mui/styles';
import Card from 'apollo-react/components/Card';
import Divider from 'apollo-react/components/Divider';
import IconButton from 'apollo-react/components/IconButton/IconButton';
import List from 'apollo-react/components/List';
import ListItem from 'apollo-react/components/ListItem';
import ListItemText from 'apollo-react/components/ListItemText';
import Menu from 'apollo-react/components/Menu';
import Popover from 'apollo-react/components/Popover';
import Tooltip from 'apollo-react/components/Tooltip';
import { Menu as MenuIcon } from 'apollo-react-icons';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

import { changeDashboardEditingState } from '../../actions/DashboardConfigActions';
import { fetchFavorites } from '../../actions/FavoritesActions';
import { setOpenHelpTopicsModal } from '../../actions/HelpTopicsActions';
import {
  fetchInstalledVersionsFromServer,
  openInstalledVersionsModal,
} from '../../actions/InstalledVersionsActions';
import { setIdMenuModalDialog, setOpenMenuModalDialog } from '../../actions/MenuDialogActions';
import { fetchCreatePatFromServer, fetchRevokePatFromServer } from '../../actions/PatActions';
import { ENTITY_DISPLAY, HELP, HOME, IQVIA, LOGOUT, TECHNICAL_SUPPORT } from '../../constants/Urls';
import Menus from '../../containers/EntityDisplay/Menus';
import { getOpenStepFormTabEntityDisplay } from '../../containers/EntityDisplay/selectors';
import { useTranslation } from '../../custom-hooks/useTranslation';
import { FORM_KEYS } from '../../form-designer/constants/form-manager.constants';
import { FormContext } from '../../form-designer/context';
import { getNetworkStatus } from '../../form-designer/selectors/common.selectors';
import { getIsRtlLanguage } from '../../selectors/AuthSelectors';
import { getIsAdminNavigationAvailable } from '../../selectors/MasterSetupPageSelectors';
import { getUITA } from '../../utils/commonUtils';

const useStyles = makeStyles({
  iconButton: {
    borderRadius: 0,
    color: '#fff',
    fontSize: '1rem',
    marginLeft: '-0.75rem',
    padding: '0.5rem 1rem',
    height: '4rem',
    width: 'auto',
    '&:hover, &[aria-owns=menu-appbar]': {
      backgroundColor: 'rgba(255, 255, 255, 0.24)',
      color: '#fff',
    },
  },
  list: {
    padding: '0.5rem 0 0',
  },
  divider: {
    margin: '0.5rem',
  },
  content: {
    display: 'flex',
  },
  mainIcon: {
    height: '1.5rem',
    width: '1.5rem',
  },
  initialsIcon: {
    '& div': {
      backgroundColor: '#0768fd',
      borderRadius: '50%',
      color: '#fff',
      fontSize: '2rem',
      height: '3.5rem',
      lineHeight: '3.75rem',
      margin: '1rem 0.5rem 1rem 1rem',
      textAlign: 'center',
      width: '3.5rem',
    },
  },
  links: {
    color: '#595959',
    display: 'flex',
    flexDirection: 'column',
    paddingRight: '0.5rem',
    paddingBottom: 8,
    '& > span:first-of-type': {
      fontWeight: 'bold',
      paddingBottom: '0',
    },
    '& div span': {
      color: '#297dfd !important',
    },
    '& a': {
      display: 'block',
      width: '100%',
    },
    '& > span, & > div': {
      padding: '0.5rem',
    },
  },
  linksRtl: {
    color: '#595959',
    display: 'flex',
    flexDirection: 'column',
    paddingRight: '0.5rem',
    paddingBottom: 8,
    left: 20,
    '& > span:first-of-type': {
      fontWeight: 'bold',
      paddingBottom: '0',
    },
    '& div span': {
      color: '#297dfd !important',
    },
    '& a': {
      display: 'block',
      width: '100%',
    },
    '& > span, & > div': {
      padding: '0.5rem',
    },
  },
  cardContent: {
    padding: 18,
    maxWidth: 200,
  },
  blackLink: {
    '& span': {
      color: '#444444 !important',
    },
  },
});

const ExternalLink = (props) => <a target="_blank" rel="noopener noreferrer" {...props} />;

const MobileDropdown = () => {
  const classes = useStyles();
  const history = useHistory();
  const { pathname } = useLocation();
  const dispatch = useDispatch();
  const translateString = useTranslation();
  const [anchorEl, setAnchorEl] = useState(null);
  const [formActionsAnchorEl, setFormActionsAnchorEl] = useState(null);
  const { isExternalUser } = useSelector((state) => state.auth);
  const isRtlLanguage = useSelector(getIsRtlLanguage);
  const openStepFormTab = useSelector(getOpenStepFormTabEntityDisplay);
  const isOnline = useSelector(getNetworkStatus);
  const isAdminNavigationAvailable = useSelector(getIsAdminNavigationAvailable);

  const username = localStorage.getItem('userFullName') || '';
  const email = localStorage.getItem('actorCodeWithDomain');
  const hasFormActions = [ENTITY_DISPLAY, ENTITY_DISPLAY].includes(pathname);

  const initials = username
    .split(' ')
    .slice(0, 2)
    .map((word) => word.charAt(0).toUpperCase())
    .join('');

  const displayChangeLanguageModal = () => {
    handleDropdownClose();
    dispatch(changeDashboardEditingState('changeLanguage'));
  };

  const handleDropdownOpen = (e) => {
    setAnchorEl(e.currentTarget);
  };

  const handleDropdownClose = () => {
    setAnchorEl(null);
  };

  const handleFormActionsClick = ({ currentTarget }) => {
    setFormActionsAnchorEl((prev) => (!prev ? currentTarget : null));
  };

  const handleLogout = (e) => {
    e.preventDefault();
    window.logout();
  };

  const handleDashboard = () => {
    history.push(HOME);
    setAnchorEl(null);
  };

  const handleCreate = () => {
    dispatch(setOpenMenuModalDialog(true));
    dispatch(setIdMenuModalDialog('Create'));
    handleDropdownClose();
  };

  const handleFind = () => {
    dispatch(setOpenMenuModalDialog(true));
    dispatch(setIdMenuModalDialog('Find'));
    handleDropdownClose();
  };

  const handleRecent = () => {
    dispatch(setOpenMenuModalDialog(true));
    dispatch(setIdMenuModalDialog('Recent'));
    handleDropdownClose();
  };

  const handleFavorites = () => {
    dispatch(setOpenMenuModalDialog(true));
    dispatch(setIdMenuModalDialog('Favorites'));
    handleDropdownClose();
  };

  const handleAdmin = () => {
    dispatch(setOpenMenuModalDialog(true));
    dispatch(setIdMenuModalDialog('ModuleAdminMenu'));
    handleDropdownClose();
  };

  const displayCreatePatModal = () => {
    handleDropdownClose();
    dispatch(fetchCreatePatFromServer());
  };

  const displayRevokePatModal = () => {
    handleDropdownClose();
    dispatch(fetchRevokePatFromServer());
  };

  const displayInstalledVersionsModal = () => {
    handleDropdownClose();
    dispatch(openInstalledVersionsModal());
  };

  const displayHelpTopicsModal = () => {
    handleDropdownClose();
    dispatch(setOpenHelpTopicsModal(true));
  };

  useEffect(() => {
    dispatch(fetchInstalledVersionsFromServer());
    if (!isExternalUser) {
      dispatch(fetchFavorites());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (openStepFormTab) {
      setAnchorEl(null);
      setFormActionsAnchorEl(null);
    }
  }, [dispatch, openStepFormTab]);

  return (
    <div ui-ta={getUITA('MenuDropDown')}>
      <Tooltip title={translateString('Account Actions')} placement="bottom">
        <IconButton
          aria-label={translateString('Account Actions')}
          aria-haspopup="true"
          aria-owns={anchorEl ? 'menu-appbar' : null}
          className={classes.iconButton}
          disableRipple
          onClick={handleDropdownOpen}
        >
          <MenuIcon className={classes.mainIcon} />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{
          horizontal: 'right',
          vertical: 'bottom',
        }}
        classes={{ list: classes.list }}
        id="MenuDropDownItemsContainer"
        onClose={handleDropdownClose}
        open={Boolean(anchorEl)}
        PaperProps={{
          square: true,
          style: isRtlLanguage
            ? {
                marginLeft: -58,
                borderTopLeftRadius: 0,
                borderTopRightRadius: 0,
                textAlign: 'left',
              }
            : {
                marginLeft: -3,
                borderTopLeftRadius: 0,
                borderTopRightRadius: 0,
              },

          'ui-ta': 'MenuDropDown',
        }}
      >
        <div className={classes.content}>
          <div className={classes.initialsIcon}>
            <div>{initials}</div>
          </div>
          <List component="nav" className={isRtlLanguage ? classes.linksRtl : classes.links}>
            <span>{username}</span>
            <span>{email}</span>

            {hasFormActions && isOnline && (
              <>
                <Divider className={classes.divider} />

                <ListItem button ui-ta="Form Actions">
                  <ListItemText
                    onClick={handleFormActionsClick}
                    primary={translateString('Form Actions')}
                  />
                </ListItem>
              </>
            )}

            <Divider className={classes.divider} />

            <ListItem button ui-ta="Dashboard">
              <ListItemText onClick={handleDashboard} primary={translateString('Dashboard')} />
            </ListItem>

            {isOnline && (
              <ListItem button ui-ta="Change Language Item">
                <ListItemText
                  onClick={displayChangeLanguageModal}
                  primary={translateString('Change Language')}
                />
              </ListItem>
            )}

            {!isExternalUser && isOnline && (
              <>
                <ListItem button ui-ta="Create PAT Item">
                  <ListItemText
                    onClick={displayCreatePatModal}
                    primary={translateString('Create PAT')}
                  />
                </ListItem>
                <ListItem button ui-ta="Revoke PAT Item">
                  <ListItemText
                    onClick={displayRevokePatModal}
                    primary={translateString('Revoke PAT')}
                  />
                </ListItem>
              </>
            )}

            {isOnline && (
              <>
                <Divider className={classes.divider} />

                <ListItem button ui-ta="Create">
                  <ListItemText onClick={handleCreate} primary={translateString('Create')} />
                </ListItem>

                <ListItem button ui-ta="Find">
                  <ListItemText onClick={handleFind} primary={translateString('Find')} />
                </ListItem>

                <ListItem button ui-ta="Recent">
                  <ListItemText onClick={handleRecent} primary={translateString('Recent')} />
                </ListItem>

                <ListItem button ui-ta="Favorites">
                  <ListItemText onClick={handleFavorites} primary={translateString('Favorites')} />
                </ListItem>

                {isAdminNavigationAvailable && (
                  <ListItem button ui-ta="ModuleAdminMenu">
                    <ListItemText onClick={handleAdmin} primary={translateString('Admin')} />
                  </ListItem>
                )}

                <Divider className={classes.divider} />

                <ListItem button onClick={handleDropdownClose}>
                  <ExternalLink href={HELP}>{translateString('Help')}</ExternalLink>
                </ListItem>

                <ListItem button ui-ta="Help Topics Item">
                  <ListItemText
                    className={classes.blackLink}
                    onClick={displayHelpTopicsModal}
                    primary={translateString('Help Topics')}
                  />
                </ListItem>

                <ListItem button onClick={handleDropdownClose}>
                  <ExternalLink href={TECHNICAL_SUPPORT}>
                    {translateString('Technical Support')}
                  </ExternalLink>
                </ListItem>

                <ListItem button ui-ta="Installed Versions Item">
                  <ListItemText
                    className={classes.blackLink}
                    onClick={displayInstalledVersionsModal}
                    primary={translateString('Installed Versions')}
                  />
                </ListItem>

                <ListItem button onClick={handleDropdownClose}>
                  <ExternalLink href={IQVIA}>{translateString('About IQVIA')}</ExternalLink>
                </ListItem>

                <Divider className={classes.divider} />

                <ListItem button>
                  <a href={LOGOUT} ui-ta={getUITA('MenuItem-2')} onClick={handleLogout}>
                    {translateString('Log out')}
                  </a>
                </ListItem>
              </>
            )}
          </List>
        </div>
      </Menu>
      <Popover
        open={!!formActionsAnchorEl}
        anchorEl={formActionsAnchorEl}
        PaperProps={{
          style: {
            boxShadow: '0 8px 20px 0 rgba(0, 0, 0, 0.08)',
          },
        }}
        onBackdropClick={() => setFormActionsAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Card className={classes.cardContent}>
          <FormContext.Provider value={FORM_KEYS.DETAIL}>
            <Menus />
          </FormContext.Provider>
        </Card>
      </Popover>
    </div>
  );
};

export default MobileDropdown;
